// outer IIFE to make sure each shared component can define its own isNodeJs.
// isNodeJs is scoped to the outer IIFE this way
(function () {

  // tests available under server/test/elementHelper/element.helper.test.js
  class ElementHelpers {
    constructor(options = {}) {
      Object.assign(this, options);
    }

    /**
     * @method findModules
     * @param modules {Object[]} - project modules or template modules
     * @param element {Object} - project or template element
     * @returns {Object}
     * */
    findModules(modules, element) {
      const returnObj = {
        module: null,
        submodule: null,
      };

      const {moduleId, submoduleId} = element;

      if (!moduleId) {
        return returnObj;
      }

      returnObj.module = modules.find((mod) => mod._id === moduleId) || null;

      if (!returnObj.module) {
        return returnObj;
      }

      returnObj.submodule = returnObj.module.submodules.find((submod) => submod._id === submoduleId) || null;

      return returnObj;
    }

    /**
     * @method isTechnicalRequirement
     * @param element {Object} - template or project element
     * @return {Boolean}
     * */
    isTechnicalRequirement(element) {
      const {projectLevel, knowledgeLevel} = element;
      return knowledgeLevel === 3 && projectLevel !== 1 && projectLevel !== 2;
    }

    /**
     * When selecting a requirementType (for example 'Functioneel') we need to filter
     * the displayed modules to only technical modules which are belong to the requirementType
     * @method filterModulesOnRequirement
     * @param options {Object}
     * @param options.modules {Object[]} - the template or project modules
     * @param options.originalModules {Object[]} - the (shallow) cloned template or project modules
     * @param options.element {Object} - the template or project element
     * @return modules {Object[]}
     * */
    filterModulesOnRequirement({modules, originalModules, element}) {
      const {requirementType} = element;
      let {module, moduleId} = element;

      // For Eis/Requirement set requirementType based on module it is placed in (e.g. Functioneel)
      if (this.isTechnicalRequirement(element)) {
        // @ts-ignore
        const requirement = this.constants.REQUIREMENT_TYPES.find((reqType) => reqType._id === requirementType);
        if (requirementType && requirement) {


          if (module && module.viewtab !== requirement.value) {
            module = null;
            moduleId = null;
          }

          return originalModules.filter((mod) => mod.viewtab === requirement.value);
        }
      }

      return modules;
    }

    /**
     * If we have a (basic)area requirement that is not technical, make sure that the input type is a paragraph
     * @method toggleInputTypeOnRequirement
     * @param options {Object}
     * @param options.element {Object} - template or project element
     * @param options.disabledFields {String[]}
     * @return {Object} - inputType and disabledFields array
     * */
    toggleInputTypeOnRequirement({element, disabledFields}) {
      const {requirementType, inputType} = element;
      // @ts-ignore
      const requirement = this.constants.REQUIREMENT_TYPES.find((reqType) => reqType._id === requirementType);
      const returnObj = {
        inputType,
        disabled: disabledFields
      };

      if (this.isTechnicalRequirement(element)) {
        if (requirement && requirementType !== 2) {
          returnObj.inputType = 'Long String';
          returnObj.disabled = Object.assign({}, disabledFields, {inputType: true});
        } else {
          returnObj.disabled = Object.assign({}, disabledFields, {inputType: false});
        }
      }

      return returnObj;
    }


    /**
     * @method availableProjectLevels
     * @param level {Number} - projectLevel that the element is called upon
     * @param appSection {String} - enum ['template', 'project']
     * @returns filteredProjectLevels {Object[]}
     * */
    availableProjectLevels(level, appSection) {
      // @ts-ignore
      return this.constants.PROJECT_LEVELS.filter(function (projectLevel) {
        if (projectLevel._id === 3) {
          return false;
        } else if (appSection === 'template') {
          return projectLevel.hasTemplateElements;
        } else if (level === 0) {
          return true;
        }

        return projectLevel._id === level;
      });
    }

    /**
     * @method filterKnowledgeLevels
     * @param element {Object} - template or project element
     * @param [str] {String} - Determine if the knowledgeLevel should be reset. Used when changing projectLevel
     * */
    filterKnowledgeLevels(element, str) {
      if (str === 'changed') {
        element.knowledgeLevel = null;
      }

      // @ts-ignore
      return this.constants.KNOWLEDGE_LEVELS.filter(function (knowledgeLevel) {
        return knowledgeLevel.projectLevels.indexOf(element.projectLevel) !== -1;
      });
    }

    /**
     * @method knowledgeLevelObject
     * @param element {Object} - template or project element
     * */
    knowledgeLevelObject(element) {
      if (!element) {
        return null;
      }

      // @ts-ignore
      return this.constants.KNOWLEDGE_LEVELS.find(function (knowledgelevel) {
        return knowledgelevel._id === element.knowledgeLevel;
      });
    }

    /**
     * @method filterInputTypes - Used when knowledgeLevel has been changed
     * @param element {Object} - template or project element
     * @param disabledFields {Object}
     * @param hiddenFields {Object}
     * @param [reset] {Boolean} - should the filtered inputTypes be reset
     * */
    filterInputTypes({element, disabledFields, hiddenFields, reset}) {
      let inputTypes;
      const knowledgeLevel = this.knowledgeLevelObject(element);

      if (!knowledgeLevel || !knowledgeLevel.compatibleInputs) {
        // @ts-ignore
        return this.constants.ELEMENT_TYPES.map((input) => {
          input.disabled = false;
          return input;
        });
      }

      // @ts-ignore
      inputTypes = this.constants.ELEMENT_TYPES.map(function (inputtype) {
        const inputType = Object.assign({}, inputtype);
        inputType.disabled = knowledgeLevel.compatibleInputs.indexOf(inputtype.value) === -1;
        return inputType;
      });

      if (element && reset) {
        inputTypes = inputTypes.filter(function (inputType) {
          return !inputType.disabled;
        });
        const elemKnowledgeLevel = element.knowledgeLevel;
        const inputType = element.inputType;
        element.inputType = inputTypes.length === 1 ? inputTypes[0].value : null;

        element.hasMultipleQualityLevels = false;
      }
      return inputTypes;
    }

    /**
     * TODO move this to module.helpers.js
     * @method getAvailableSubmodules - returns array of all submodules of modules provided
     * @param modules {Object[]}
     * */
    getAvailableSubmodules(modules) {
      return modules.reduce((acc, item) => [...acc, ...item.submodules], []);
    }

    /**
     * TODO move this to sg-table or sg-table-helper
     * */
    constantDisplayName(id, item, column) {
      const constant = column.filterData.find((c) => c._id === id);
      return constant ? constant.displayName : '';
    }

    filterElements(vm) {
      vm.filteredElements = vm.elements.filter((e) => !e.hide && e.active);
      vm.filteredElementsLength = vm.filteredElements.length;

      return vm.filteredElements;
    }

    nextElement(vm, $scope) {
      vm.filteredElementsIndex += 1;
      vm.selectedElement = this.filterElements(vm)[vm.filteredElementsIndex];
      $scope.$broadcast('newElement', vm.selectedElement);
    }

    prevElement(vm, $scope) {
      vm.filteredElementsIndex -= 1;
      vm.selectedElement = this.filterElements(vm)[vm.filteredElementsIndex];
      $scope.$broadcast('newElement', vm.selectedElement);
    }

    /**
     * TODO move this to sg-table/sg-table-helper or modules.helper.js
     * */
    modulesFiltered(vm, column) {
      const selectedModules = column.filterData.filter((item) => item.selected);
      const submodColumn = vm.columns.find((col) => col.displayKey === 'submoduleId');
      if (selectedModules.length === 1) {
        vm.availableSubmodules = this.getAvailableSubmodules(selectedModules);
        submodColumn.type = 'multiSelect';
        submodColumn.filterData = vm.availableSubmodules.map((submod) => {
          submod.selected = true;
          return submod;
        });
      } else {
        submodColumn.type = null;
        submodColumn.filterData = [];
        submodColumn.isAllSelected = true;
        submodColumn.value = undefined;
        vm.availableSubmodules = this.getAvailableSubmodules(vm.modules);
        vm.availableSubmodules.forEach((submod) => submod.selected = true);
      }
    }

    /**
     * TODO find a better place for this function. It is too general for this helper file.
     * */
    getNameById(arr, id) {
      const item = arr.find((m) => m._id === id);
      return item ? item.name : '';
    }

    elementTypeDisplayName(displayKey) {
      // @ts-ignore
      const inputType = this.constants.ELEMENT_TYPES.find((type) => type.value === displayKey);
      return inputType.displayName || displayKey;
    }

    getElementValue(element) {
      if (element.hasMultipleQualityLevels) {
        return element.qualityLevel[element.quality];
      }
      return element.qualityLevel.all;
    }


    /**
     * @method removeCopyProperties - Back-end only, removes unused properties so a clean copied element is created
     * @param element {Object}
     * */
    removeCopyProperties(element, req) {
      if (element.action === 'copy') {
        delete element._id;
        delete element.created;
        delete element.updated;
        delete element.createdBy;
        delete element.changes;
        delete element.comments;
        delete element.uploads;

        if (element.comment) {
          element.comments = [{
            text: element.comment,
            userId: req.user._id
          }];
        }
      }
    }

    /**
     * @method removeCopyProperties - Back-end only, removes unused properties so a clean copied element is created
     * @param context {Object}
     * */
    updateConstruction(context) {
      const {project} = context;
      const {element} = context.req.body;

      if (element.projectLevel === 5) {
        // @ts-ignore
        const construction = this.ConstructionHelpers.getConstructionByConstructionId({
          project,
          constructionId: element.constructionId,
          options: {
            versionId: context.req.body.versionId,
          }
        });

        construction.hasSheet = true;
      }
    }
  }


  exports.ElementHelpers = ElementHelpers;

})();
